import React, { useEffect, useMemo, useState } from "react";
import { getDownloadURL, ref } from "firebase/storage";

import { fetchJsonWithAuth } from "../api/client";
import { useAdminAuth } from "../auth/AdminAuthProvider";
import { AdminProtectedRoute } from "../auth/AdminProtectedRoute";
import { AdminShell } from "../components/AdminShell";
import { buildCacheKey, buildPagePrefix, getFreshCache, invalidateByPrefix, setCache } from "../shared/adminCache";
import { StorageRow, StorageSummary } from "../shared/siteContent";
import { storage } from "../../firebaseConfig";

type Notice = { type: "ok" | "warn" | "error"; message: string } | null;

type StorageResponse = {
  success: boolean;
  rows: StorageRow[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  summary: StorageSummary;
};

const ADMIN_CACHE_TTL_MS = 2 * 60 * 1000;

const emptySummary: StorageSummary = {
  totalCount: 0,
  totalSize: 0,
  popupCount: 0,
  popupSize: 0,
  irCount: 0,
  irSize: 0,
  staleCount: 0,
};

// Storage API 응답을 안전한 기본값 포함 형태로 정규화한다.
function normalizeStorageResponse(response: Partial<StorageResponse> | null | undefined) {
  if (!response || typeof response !== "object") {
    return {
      rows: [] as StorageRow[],
      summary: emptySummary,
      page: 1,
      pageSize: 10,
      totalPages: 1,
      usedFallback: true,
    };
  }

  return {
    rows: Array.isArray(response.rows) ? response.rows : [],
    summary: response.summary || emptySummary,
    page: response.page || 1,
    pageSize: response.pageSize || 10,
    totalPages: response.totalPages || 1,
    usedFallback: false,
  };
}

// 바이트 값을 사람이 읽기 쉬운 단위 문자열(B/KB/MB)로 변환한다.
function formatBytes(bytes: number) {
  if (!bytes) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ISO/UTC 기반 시간을 화면 표시용 yyyy-MM-dd HH:mm 문자열로 변환한다.
function formatDate(value: string) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  const pad = (num: number) => String(num).padStart(2, "0");
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())} ${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
}

/**
 * Storage 관리 페이지.
 * 파일 목록 조회/필터링/선택 삭제와 stale 자동 선택 흐름을 제공한다.
 */
export function StoragePage() {
  const { getIdToken, ready, user } = useAdminAuth();
  const [rows, setRows] = useState<StorageRow[]>([]);
  const [summary, setSummary] = useState<StorageSummary>(emptySummary);
  const [notice, setNotice] = useState<Notice>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    q: "",
    cat: "ALL",
    usage: "ALL",
    sort: "latest",
  });

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  // Storage 목록을 필터/페이지 기준으로 로드하고 캐시 동기화 상태를 관리한다.
  async function loadRows(
    targetPage = page,
    targetPageSize = pageSize,
    targetFilters = filters,
    options?: { forceNetwork?: boolean },
  ) {
    const forceNetwork = Boolean(options?.forceNetwork);
    const queryKey = JSON.stringify({
      q: targetFilters.q,
      cat: targetFilters.cat,
      usage: targetFilters.usage,
      sort: targetFilters.sort,
      page: targetPage,
      pageSize: targetPageSize,
    });
    const cacheKey = user ? buildCacheKey(user.uid, "storage", queryKey) : "";
    let hasWarmData = false;
    // Storage 화면은 Functions가 계산한 사용중 여부와 stale 여부를 그대로 받는다.
    setNotice(null);
    if (!forceNetwork && cacheKey) {
      const cached = getFreshCache<{
        rows: StorageRow[];
        summary: StorageSummary;
        page: number;
        pageSize: number;
        totalPages: number;
      }>(cacheKey, ADMIN_CACHE_TTL_MS);
      if (cached?.data) {
        setRows(cached.data.rows);
        setSummary(cached.data.summary);
        setPage(cached.data.page || targetPage);
        setPageSize(cached.data.pageSize || targetPageSize);
        setTotalPages(cached.data.totalPages || 1);
        setSelected([]);
        hasWarmData = true;
      }
    }
    setLoading(!hasWarmData);
    setSyncing(hasWarmData);
    if (hasWarmData) {
      setNotice({ type: "warn", message: "서버 최신 정보를 동기화 중입니다." });
    }
    try {
      const query = new URLSearchParams({
        q: targetFilters.q,
        cat: targetFilters.cat,
        usage: targetFilters.usage,
        sort: targetFilters.sort,
        page: String(targetPage),
        pageSize: String(targetPageSize),
      });
      const response = await fetchJsonWithAuth<StorageResponse>(`/api/admin/storage?${query.toString()}`, { method: "GET" }, getIdToken);
      const normalized = normalizeStorageResponse(response);
      setRows(normalized.rows);
      setSummary(normalized.summary);
      setPage(normalized.page || targetPage);
      setPageSize(normalized.pageSize || targetPageSize);
      setTotalPages(normalized.totalPages || 1);
      if (cacheKey) {
        setCache(cacheKey, {
          rows: normalized.rows,
          summary: normalized.summary,
          page: normalized.page || targetPage,
          pageSize: normalized.pageSize || targetPageSize,
          totalPages: normalized.totalPages || 1,
        });
      }
      if (normalized.usedFallback) {
        setNotice({ type: "warn", message: "storage 응답이 비어 있어 빈 목록으로 표시 중입니다." });
      } else {
        setNotice(null);
      }
      setSelected([]);
    } catch (caught) {
      if (hasWarmData) {
        setNotice({ type: "warn", message: "캐시 데이터를 표시 중이며 최신 동기화에 실패했습니다." });
      } else {
        setNotice({ type: "error", message: caught instanceof Error ? caught.message : "Storage 목록 조회에 실패했습니다." });
      }
    } finally {
      setLoading(false);
      setSyncing(false);
    }
  }

  useEffect(() => {
    if (!ready || !user) return;
    void loadRows();
  }, [filters, page, pageSize, ready, user?.uid]);

  // Storage 경로를 다운로드 URL로 변환해 새 탭에서 연다.
  async function openFile(pathValue: string) {
    try {
      // 관리자에서는 저장 경로만 알고 있어도 Firebase Storage에서 즉시 미리 열어볼 수 있다.
      const url = await getDownloadURL(ref(storage, pathValue));
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (caught) {
      setNotice({ type: "error", message: caught instanceof Error ? caught.message : "파일 열기에 실패했습니다." });
    }
  }

  // 선택된 Storage 경로를 관리자 API를 통해 일괄 삭제한다.
  async function deletePaths(paths: string[]) {
    if (!paths.length) return;
    const confirmed = window.confirm(paths.length > 1 ? `선택한 ${paths.length}개 파일을 삭제하시겠습니까?` : "이 파일을 삭제하시겠습니까?");
    if (!confirmed) return;
    try {
      // 삭제는 클라이언트가 직접 하지 않고 관리자 API를 통해 일괄 처리한다.
      setLoading(true);
      const response = await fetchJsonWithAuth<{ success: boolean; deleted: string[]; failed: Array<{ path: string; message: string }> }>(
        "/api/admin/storage",
        {
          method: "DELETE",
          body: JSON.stringify({ paths }),
          headers: {
            "Content-Type": "application/json",
          },
        },
        getIdToken,
      );

      const failed = Array.isArray(response?.failed) ? response.failed : [];
      const deleted = Array.isArray(response?.deleted) ? response.deleted : [];
      if (failed.length) {
        setNotice({ type: "warn", message: `일부 삭제 실패: ${failed[0]?.message || "unknown"}` });
      } else {
        setNotice({ type: "ok", message: `${deleted.length}개 파일을 삭제했습니다.` });
      }

      if (user?.uid) {
        invalidateByPrefix(buildPagePrefix(user.uid, "storage"));
      }
      await loadRows(page, pageSize, filters, { forceNetwork: true });
    } catch (caught) {
      setNotice({ type: "error", message: caught instanceof Error ? caught.message : "파일 삭제에 실패했습니다." });
    } finally {
      setLoading(false);
    }
  }

  // 서버 기준 "미사용"(!isInUse && !stale) 파일을 조회해 현재 선택 목록으로 채운다.
  async function autoSelectStale() {
    try {
      // 미사용 자동 선택은 "미사용(오래됨)"을 제외한 순수 미사용 상태만 선택한다.
      const query = new URLSearchParams({
        q: "",
        cat: "ALL",
        usage: "STORED",
        sort: "latest",
        page: "1",
        pageSize: "5000",
      });
      const response = await fetchJsonWithAuth<StorageResponse>(`/api/admin/storage?${query.toString()}`, { method: "GET" }, getIdToken);
      const rows = Array.isArray(response?.rows) ? response.rows : [];
      const unusedPaths = rows.filter((row) => !row.isInUse && !row.stale).map((row) => row.path);
      setSelected(unusedPaths);
      setNotice(unusedPaths.length ? { type: "ok", message: `${unusedPaths.length}개 미사용 파일을 선택했습니다.` } : { type: "warn", message: "선택할 미사용 파일이 없습니다." });
    } catch (caught) {
      setNotice({ type: "error", message: caught instanceof Error ? caught.message : "자동 선택에 실패했습니다." });
    }
  }

  return (
    <AdminProtectedRoute>
      <AdminShell title="파일 관리" description="Storage 객체와 site_config 참조 경로를 비교해 사용 여부와 삭제 후보를 관리합니다.">
        {notice ? <div className={`cm-notice ${notice.type === "error" ? "error" : notice.type === "warn" ? "warn" : ""}`}>{notice.message}</div> : null}

        <div className="cm-kv">
          <div className="cm-kv-card">
            <span className="cm-muted">Total Usage</span>
            <strong>{formatBytes(summary.totalSize)}</strong>
            <div className="cm-muted">{summary.totalCount} files</div>
          </div>
          <div className="cm-kv-card">
            <span className="cm-muted">IR PDF</span>
            <strong>{formatBytes(summary.irSize)}</strong>
            <div className="cm-muted">{summary.irCount} files</div>
          </div>
          <div className="cm-kv-card">
            <span className="cm-muted">Popup Images</span>
            <strong>{formatBytes(summary.popupSize)}</strong>
            <div className="cm-muted">{summary.popupCount} files</div>
          </div>
          <div className="cm-kv-card">
            <span className="cm-muted">Cleanup Target</span>
            <strong>{summary.staleCount}</strong>
            <div className="cm-muted">stale files</div>
          </div>
        </div>

        <div className="cm-filter-grid">
          <div className="cm-filter-item cm-filter-item-grow">
            <label className="cm-filter-label" htmlFor="cm-storage-filter-q">
              검색
            </label>
            <input
              className="cm-input"
              id="cm-storage-filter-q"
              onChange={(event) => {
                setPage(1);
                setFilters((prev) => ({ ...prev, q: event.target.value }));
              }}
              placeholder="파일명/경로 검색"
              value={filters.q}
            />
          </div>
          <div className="cm-filter-item">
            <label className="cm-filter-label" htmlFor="cm-storage-filter-cat">
              카테고리
            </label>
            <select className="cm-select" id="cm-storage-filter-cat" onChange={(event) => setFilters((prev) => ({ ...prev, cat: event.target.value }))} value={filters.cat}>
              <option value="ALL">전체</option>
              <option value="POPUP">Popup</option>
              <option value="IR">IR</option>
            </select>
          </div>
          <div className="cm-filter-item">
            <label className="cm-filter-label" htmlFor="cm-storage-filter-usage">
              사용상태
            </label>
            <select className="cm-select" id="cm-storage-filter-usage" onChange={(event) => setFilters((prev) => ({ ...prev, usage: event.target.value }))} value={filters.usage}>
              <option value="ALL">전체</option>
              <option value="IN_USE">사용중</option>
              <option value="STORED">미사용</option>
            </select>
          </div>
          <div className="cm-filter-item">
            <label className="cm-filter-label" htmlFor="cm-storage-filter-sort">
              정렬
            </label>
            <select className="cm-select" id="cm-storage-filter-sort" onChange={(event) => setFilters((prev) => ({ ...prev, sort: event.target.value }))} value={filters.sort}>
              <option value="latest">최신 업로드</option>
              <option value="oldest">오래된 업로드</option>
              <option value="in_use_first">사용중 우선</option>
              <option value="size_desc">큰 파일 우선</option>
              <option value="size_asc">작은 파일 우선</option>
              <option value="name_asc">파일명</option>
            </select>
          </div>
          <div className="cm-filter-item">
            <label className="cm-filter-label" htmlFor="cm-storage-filter-size">
              개수
            </label>
            <select className="cm-select" id="cm-storage-filter-size" onChange={(event) => setPageSize(Number(event.target.value))} value={pageSize}>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>
          <div className="cm-filter-item cm-filter-item-full">
            <span className="cm-filter-label">동작</span>
            <div className="cm-actions">
              <button className="cm-btn cm-btn-refresh" disabled={loading} onClick={() => void loadRows(1, pageSize, filters, { forceNetwork: true })} type="button">
                새로고침
              </button>
              <button className="cm-btn cm-btn-secondary" onClick={() => void autoSelectStale()} type="button">
                미사용 자동 선택
              </button>
              <button className="cm-btn cm-btn-danger" disabled={!selected.length} onClick={() => void deletePaths(selected)} type="button">
                선택 삭제
              </button>
            </div>
          </div>
        </div>

        {loading ? <div className="cm-page-loader">Storage 목록을 불러오는 중입니다...</div> : null}
        {!loading && syncing ? <div className="cm-loading-line">서버 최신 정보를 동기화 중입니다...</div> : null}

        <div className="cm-table-wrap">
          <table className="cm-table">
            <thead>
              <tr>
                <th>
                  <input
                    checked={rows.length > 0 && rows.every((row) => selectedSet.has(row.path))}
                    onChange={(event) => setSelected(event.target.checked ? rows.map((row) => row.path) : [])}
                    type="checkbox"
                  />
                </th>
                <th>파일</th>
                <th>카테고리</th>
                <th>크기</th>
                <th>업로드일</th>
                <th>상태</th>
                <th>액션</th>
              </tr>
            </thead>
            <tbody>
              {!rows.length && !loading ? (
                <tr>
                  <td colSpan={7}>조건에 맞는 파일이 없습니다.</td>
                </tr>
              ) : null}
              {rows.map((row) => (
                <tr key={row.path}>
                  <td data-label="선택">
                    <input
                      checked={selectedSet.has(row.path)}
                      onChange={(event) =>
                        setSelected((prev) => (event.target.checked ? Array.from(new Set([...prev, row.path])) : prev.filter((value) => value !== row.path)))
                      }
                      type="checkbox"
                    />
                  </td>
                  <td data-label="파일">
                    <strong>{row.title}</strong>
                    <div className="cm-muted">{row.meta}</div>
                    <div className="cm-path">{row.path}</div>
                  </td>
                  <td data-label="카테고리">
                    <span className="cm-pill">{row.catLabel}</span>
                  </td>
                  <td data-label="크기">{formatBytes(row.size)}</td>
                  <td data-label="업로드일">{formatDate(row.updated)}</td>
                  <td data-label="상태">
                    {row.isInUse ? <span className="cm-pill ok">사용중</span> : <span className="cm-pill">{row.stale ? "미사용(오래됨)" : "미사용"}</span>}
                  </td>
                  <td data-label="액션">
                    <div className="cm-actions">
                      <button className="cm-btn cm-btn-secondary" onClick={() => void openFile(row.path)} type="button">
                        열기
                      </button>
                      <button className="cm-btn cm-btn-danger" onClick={() => void deletePaths([row.path])} type="button">
                        삭제
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="cm-actions" style={{ marginTop: 16 }}>
          <button className="cm-btn cm-btn-secondary" disabled={page <= 1} onClick={() => setPage((prev) => Math.max(1, prev - 1))} type="button">
            Prev
          </button>
          <span className="cm-pill">
            {page} / {totalPages}
          </span>
          <button className="cm-btn cm-btn-secondary" disabled={page >= totalPages} onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))} type="button">
            Next
          </button>
        </div>
      </AdminShell>
    </AdminProtectedRoute>
  );
}
